import { BlogHubPage } from '@/lib/pages/blog/BlogHubPage';
import { BlogUnavailablePage } from '@/lib/pages/blog/BlogUnavailablePage';
import {
  isBlogUnavailable,
  type BlogUnavailableProps,
} from '@/lib/pages/blog/blog-unavailable';
import { blogUnavailableProps } from '@/lib/pages/blog/blog-unavailable-props';
import {
  settleBlogPrimary,
  settleBlogValue,
} from '@/lib/pages/blog/settle-blog-primary';

import {
  BlogPublicHub,
  BlogPublicPost,
  fetchBlogHub,
  fetchBlogLatest,
  parseUiPage,
  SpringPage,
  toApiPage,
} from '@/lib/entities/blog';
import { fetchPageSeoOrNull, PageSeo } from '@/lib/entities/seo';
import { SeoEntries } from '@/lib/entities/seo/lib/seo-entries';

import { GetServerSideProps } from 'next';
import { i18n, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { isDevelopment } from '../../lib/config';

const EMPTY_PAGE: SpringPage<BlogPublicPost> = {
  content: [],
  totalElements: 0,
  totalPages: 0,
  number: 0,
};

type ReadyProps = {
  hub: BlogPublicHub;
  latest: SpringPage<BlogPublicPost>;
  uiPage: number;
  pageSeo: PageSeo | null;
  locale: string;
};

type Props = ReadyProps | BlogUnavailableProps;

export default function BlogIndexPage(props: Props) {
  if (isBlogUnavailable(props)) {
    return <BlogUnavailablePage path={props.path} />;
  }

  return <BlogIndexView {...props} />;
}

const BlogIndexView = ({
  hub,
  latest,
  uiPage,
  pageSeo,
  locale,
}: ReadyProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('index', { keyPrefix: 'meta' });

  return (
    <>
      <SeoEntries
        fallback={{
          title: t('blog.title'),
          description: t('blog.description'),
          openGraphImage: '/images/loyalty-program/og.webp',
        }}
        seo={pageSeo}
        language={language}
        path="/blog"
        robots={uiPage > 1 ? 'noindex, follow' : undefined}
      />
      <main>
        <BlogHubPage
          hub={hub}
          latest={latest}
          uiPage={uiPage}
          locale={locale}
          title={pageSeo?.h1?.trim() || undefined}
        />
      </main>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
  query,
  res,
}) => {
  if (locale === 'default') {
    return { notFound: true };
  }

  if (isDevelopment) {
    await i18n?.reloadResources();
  }

  const lang = locale ?? 'ru';
  const uiPage = parseUiPage(query.page);
  const [hubOutcome, latestOutcome] = await Promise.all([
    settleBlogPrimary(() => fetchBlogHub(lang)),
    settleBlogValue(() => fetchBlogLatest(lang, toApiPage(uiPage)), EMPTY_PAGE),
  ]);

  if (
    hubOutcome.status === 'unavailable' ||
    latestOutcome.status === 'unavailable'
  ) {
    return { props: await blogUnavailableProps(res, lang, '/blog') };
  }

  if (hubOutcome.status === 'notFound') {
    return { notFound: true };
  }

  const pageSeo = await fetchPageSeoOrNull({ path: '/blog', language: lang });

  return {
    props: {
      ...(await serverSideTranslations(lang, ['index'])),
      headerTheme: 'light',
      hub: hubOutcome.data,
      latest: latestOutcome.data,
      uiPage,
      pageSeo,
      locale: lang,
    },
  };
};
